import { gql, useApolloClient, useSubscription } from '@apollo/client';
import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import logger from '@orn/shared-web/lib/logger';
import * as React from 'react';

import { WorkItemStatus } from '../__generated__/globalTypes';
import { evictWorkItemCounts, evictWorkItemReads, syncWorkItemChips } from '../providers/apolloCache';

import { useFeatureGate } from './useFeatureGates';
import { useWsReconnectEffect } from './useWsReconnectEffect';

import type {
  WorkItemUpdatedSubscription,
  WorkItemUpdatedSubscriptionVariables,
} from './__generated__/useWorkItemLiveUpdates.gql';

/**
 * The card fields land on the normalized `WorkItem`, so an open Center updates
 * copy and badges in place; membership and counts ride the evictions below,
 * which a payload cannot express. Two selections earn their place beyond that:
 * `status` and `needsRecheck`, the membership discriminators, derived on the
 * server exactly as the list queries derive them; and the item's current links —
 * `transactions { id }`, `bills { id }`, and `recurringTransaction { id }` —
 * which the chip sync writes onto each linked record. No other relation is
 * selected — the rest is re-read rather than pushed.
 */
const WORK_ITEM_LIVE_FIELDS = gql`
  fragment WorkItemLiveFields on WorkItem {
    id
    title
    body
    summary
    category
    read
    status
    needsRecheck
    dueDate
    createdAt
    updatedAt
    transactions {
      id
    }
    bills {
      id
    }
    recurringTransaction {
      id
    }
  }
`;

const WORK_ITEM_UPDATED_SUBSCRIPTION = gql`
  subscription WorkItemUpdated {
    workItemUpdated {
      ...WorkItemLiveFields
    }
  }
  ${WORK_ITEM_LIVE_FIELDS}
`;

/**
 * How long to hold evictions after a delivery so a producer burst (a sweep
 * archiving several items, a bulk mark-read) coalesces into one refetch wave
 * instead of one per event.
 */
const PUSH_COALESCE_MS = 750;

interface IPendingWorkItemEvent {
  id: string;
  status: WorkItemStatus;
  needsRecheck: boolean;
  read: boolean;
  transactionIds: string[];
  billIds: string[];
  recurringTransactionId: string | null;
}

/** Neither archived nor waiting on a recheck — the two list-membership conditions a delivery carries. */
const isListed = (event: IPendingWorkItemEvent): boolean =>
  event.status !== WorkItemStatus.Archived && !event.needsRecheck;

/**
 * Live work-item ("alerts") updates for the whole app — mount once in the app
 * shell, inside the authenticated tree, beside `useFinancialAccountLiveUpdates`.
 *
 * An entity push cannot add or remove list members or move aggregate counts,
 * so a delivery is treated as a doorbell: the pushed scalars land on the
 * normalized `WorkItem`, and this hook evicts the work-item reads through the
 * same centralized seam the mutation handlers use. Which reads depends on
 * what the delivery implies. Only a row becoming archived or held for a
 * recheck, or coming back from either, changes membership and evicts list + counts;
 * an unseen *unread* id is treated the same way, conservatively, since it may
 * be new. Everything else evicts counts only — a read flip, a copy edit, or a
 * PENDING/ACTIVE move, which re-sections the Center from the normalized entity
 * without changing membership. Evicting the list collapses the Center's loaded
 * pages, and the most frequent event on this topic is the viewer's own
 * mark-read echoing back — usually on a row that predates the socket, which is
 * why "unseen but already read" cannot be treated as new.
 *
 * Unlike `useFinancialAccountLiveUpdates`, this hook owns no reconciliation
 * query: the sidebar's counts poll is this surface's permanent floor (the
 * chrome never unmounts), and evicted reads refetch on their own next render.
 * Delivery is at-most-once with no replay, so a ws reconnect evicts both
 * reads outright and forgets what it has seen — events lost in the gap can
 * never be classified, and an unseen-again id must over-evict, not under.
 *
 * @returns nothing; the hook exists for its subscription and cache effects.
 */
export const useWorkItemLiveUpdates = (): void => {
  const subscriptionsEnabled = useFeatureGate(FeatureGate.GraphqlSubscriptions);
  // The same gate the bell and the Center read: it decides whether the alert
  // surfaces exist at all, so this hook must go dark with them.
  const alertsEnabled = useFeatureGate(FeatureGate.AlertsCenter);
  const enabled = subscriptionsEnabled && alertsEnabled;

  const client = useApolloClient();
  const lastListedByIdRef = React.useRef(new Map<string, boolean>());
  const pendingRef = React.useRef<IPendingWorkItemEvent[]>([]);
  const flushTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushPending = React.useCallback(() => {
    flushTimerRef.current = null;
    const batch = pendingRef.current;
    pendingRef.current = [];
    if (batch.length === 0) {
      return;
    }
    const lastListedById = lastListedByIdRef.current;
    // Only a crossing of the listed line — archived, or held back for a recheck
    // — moves a row in or out of the lists. A PENDING/ACTIVE change re-sections the Center
    // and moves counts, but the pushed `status` lands on the normalized entity
    // and re-renders it in place — evicting the list for that would collapse
    // loaded pages to page one for a row that never left the list.
    const membershipChanged = batch.some((event) => {
      const last = lastListedById.get(event.id);
      if (last !== undefined) {
        return last !== isListed(event);
      }
      // First delivery for this id on this socket — which is what the rows a
      // user clicks look like, since they usually predate the socket. An
      // already-read row cannot be a new item (creation starts unread), so
      // unless it arrives unlisted this is an existing row's echo, not a
      // membership change; a wrong guess here only delays the list until the
      // Center's counts-transition resync or the poll floor.
      return !event.read || !isListed(event);
    });
    for (const event of batch) {
      lastListedById.set(event.id, isListed(event));
      // Mirror the mutation path's chip treatment so an archive arriving from
      // another session clears the linked records' `workItem` the same way a
      // local archive does — the evictions below never reach record reads.
      syncWorkItemChips(client.cache, {
        id: event.id,
        archived: !isListed(event),
        transactionIds: event.transactionIds,
        billIds: event.billIds,
        recurringTransactionId: event.recurringTransactionId,
      });
    }
    if (membershipChanged) {
      evictWorkItemReads(client.cache);
    } else {
      evictWorkItemCounts(client.cache);
    }
  }, [client]);

  useSubscription<WorkItemUpdatedSubscription, WorkItemUpdatedSubscriptionVariables>(WORK_ITEM_UPDATED_SUBSCRIPTION, {
    skip: !enabled,
    onData: ({ data }) => {
      const workItem = data.data?.workItemUpdated;
      if (!workItem) {
        return;
      }
      pendingRef.current.push({
        id: workItem.id,
        status: workItem.status,
        needsRecheck: workItem.needsRecheck,
        read: workItem.read,
        transactionIds: workItem.transactions.map((transaction) => transaction.id),
        billIds: workItem.bills.map((bill) => bill.id),
        recurringTransactionId: workItem.recurringTransaction?.id ?? null,
      });
      flushTimerRef.current ??= setTimeout(flushPending, PUSH_COALESCE_MS);
    },
    onError: (error) => {
      logger.warn(`[work-item-live] subscription error: ${String(error)}`);
    },
  });

  // Clear a scheduled flush and its batch when the hook unmounts or `enabled`
  // flips; the dropped batch is exactly what the poll floor exists to
  // reconcile, and a gate turned off mid-session must not evict afterward.
  React.useEffect(() => {
    return () => {
      if (flushTimerRef.current !== null) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      pendingRef.current = [];
    };
  }, [enabled]);

  useWsReconnectEffect(enabled, () => {
    // Drop any straggler batch along with the map: a flush firing after the
    // forget would re-seed pre-gap statuses, and a post-gap delivery matching
    // one would then read as "no change" across states this hook never saw.
    if (flushTimerRef.current !== null) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    pendingRef.current = [];
    lastListedByIdRef.current.clear();
    evictWorkItemReads(client.cache);
  });
};
